// One icon set (#2043, ruled 2026-10-27): every icon the SPA draws — the rail's
// workspaces and pages, the device acts, and the controls — comes from
// `lucide-react` through this module, and nowhere else imports it. That is what
// keeps the set one: one size per place and one stroke for all, inlined SVG
// that renders the same on every platform, where the Unicode glyphs it replaces
// rendered in whatever font the operating system had.
//
// **Every icon is decorative.** `Glyph` always draws `aria-hidden`, with the
// word beside it or on its control's accessible name, so nothing an operator
// can do or read is told by a picture alone (ZENITH.md §18). A vertical's own
// icon for a lexicon word (ADR-0484) is chosen from this set in the lexicon's
// table, beside the word (#2046); this module names no vertical.
import type { LucideIcon } from "lucide-react";

export type { LucideIcon };
export {
  // The rail's workspaces.
  House as HomeIcon, Building2 as AccountsIcon, Activity as OperateIcon, PencilRuler as DesignIcon, Settings as AdminIcon,
  // The rail's pages.
  LayoutDashboard as OverviewIcon, Landmark as OrganizationsIcon, Briefcase as CustomersIcon, MapPin as SitesIcon,
  Cable as WiredIcon, Router as DevicesIcon, Wifi as WirelessIcon, KeyRound as AccessIcon, CircleArrowUp as FirmwareIcon,
  Map as PhysicalIcon, Layers as VlansIcon, DoorOpen as PortalsIcon, Globe as GatewayIcon, FileDiff as ChangesIcon,
  Import as CarryOverIcon, Boxes as DeviceModelsIcon, Plug as IntegrationsIcon, Users as UsersIcon, LogIn as SignInMethodsIcon,
  Bot as ServicePrincipalsIcon, ScrollText as AuditIcon, Server as CellsIcon, HardDrive as HardwareIcon, Package as StockIcon,
  Tag as BrandsIcon, ListChecks as BrandReviewIcon, ChartColumn as ReportIcon, MapPinned as FleetMapIcon,
  // The pictures a vertical draws beside a lexicon word (ADR-0484 decision 5), chosen in
  // `lexicon.ts`'s table and nowhere else.
  Hotel as HotelIcon, HouseHeart as HouseHeartIcon, Building as BuildingIcon, BriefcaseBusiness as OfficeIcon,
  Store as StoreIcon, BedDouble as BedIcon, HouseWifi as HouseWifiIcon, Network as NetworkIcon,
  ScanBarcode as ScannerIcon, ConciergeBell as ConciergeBellIcon, HandHeart as HandHeartIcon,
  Dumbbell as AmenityIcon, IdCard as BadgeIcon, ShoppingBag as ShoppingBagIcon,
  // The device acts (`ui.tsx`'s `Icon`).
  RotateCcw as RebootIcon, Lightbulb as BlinkIcon, Factory as FactoryIcon, SquareTerminal as ConsoleIcon,
  Pencil as EditIcon, Trash2 as DeleteIcon, Ellipsis as MoreIcon, RefreshCw as RefreshIcon, EthernetPort as PortsIcon,
  FilePlus2 as PlanIcon, Power as PowerIcon,
  // The controls.
  Info as InfoIcon, ChevronDown as OpenIcon, ChevronRight as ClosedIcon, ChevronUp as AscendingIcon,
  Search as SearchIcon, X as CloseIcon, Check as DoneIcon, TriangleAlert as AttentionIcon, ArrowUpDown as LinkEndIcon, ExternalLink as ElsewhereIcon,
  // The in-app report (ADR-0518 1a).
  MessageSquareWarning as ReportProblemIcon,
  Copy as CopyIcon,
} from "lucide-react";

/** The sizes the SPA draws an icon at: the rail's workspaces at 20, a mark
 * inside a line of small text (a sort mark, a caret) at 12, everything else at
 * 16. The stroke is one for all. */
export type GlyphSize = 12 | 16 | 20;
export const STROKE = 1.75;

export function Glyph({ icon: I, size = 16, className }: { icon: LucideIcon; size?: GlyphSize; className?: string }) {
  return <I size={size} strokeWidth={STROKE} absoluteStrokeWidth aria-hidden="true" focusable="false" className={className ? `glyph ${className}` : "glyph"} />;
}
